/**
 * PaymentRequired Component
 *
 * Displays a user-friendly message when a workflow fails due to insufficient balance.
 * Provides options to top up balance or retry after payment.
 */

import React from 'react';
import { AlertCircle, CreditCard, RefreshCw } from 'lucide-react';
import { Button } from './ui';

export function PaymentRequired({
  paymentData,
  onRetry,
  onClose
}) {
  if (!paymentData) return null;

  const { workflowId, estimatedCost, redirectUrl, message } = paymentData;

  const handleGoToBilling = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      window.location.href = `/billing${workflowId ? `?workflow_id=${workflowId}` : ''}`;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl border border-app-border max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="flex-shrink-0 w-10 h-10 bg-orange-50 rounded-full flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-orange-600" strokeWidth={2} />
          </div>
          <div className="flex-1 pt-0.5">
            <h2 className="text-lg font-semibold text-app-text mb-1">
              Payment required
            </h2>
            <p className="text-sm text-app-text-muted">
              {message || 'Insufficient balance to complete this workflow.'}
            </p>
          </div>
        </div>

        {estimatedCost && (
          <div className="bg-app-panel-muted border border-app-border rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-app-text">Estimated cost</span>
              <span className="text-base font-semibold text-app-text tabular-nums">
                £{Number(estimatedCost).toFixed(4)}
              </span>
            </div>
          </div>
        )}

        <p className="text-sm text-app-text-muted mb-6">
          Top up your account to continue with this workflow.
        </p>

        <div className="flex gap-3">
          <Button variant="primary" size="md" className="flex-1" onClick={handleGoToBilling}>
            <CreditCard className="w-4 h-4" />
            Top up balance
          </Button>

          {onRetry && workflowId && (
            <Button variant="secondary" size="md" className="flex-1" onClick={onRetry}>
              <RefreshCw className="w-4 h-4" />
              Retry
            </Button>
          )}
        </div>

        {onClose && (
          <Button variant="ghost" size="sm" className="mt-3 w-full" onClick={onClose}>
            Close
          </Button>
        )}

        {workflowId && (
          <div className="mt-5 pt-4 border-t border-app-border">
            <p className="text-xs text-app-text-subtle">
              Workflow ID: <span className="font-mono">{workflowId}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentRequired;
